import { useState } from "react";
import { Reveal, Eyebrow } from "./Reveal";
import { HotelGallery } from "./HotelGallery";
import { BudgetCalculator } from "./BudgetCalculator";
import { HOTELS, PICTOGRAMS, HOTEL_GALLERIES } from "../../data";
import { MapPin, Star, Sparkles, Utensils, Activity, HeartHandshake, Gift, Smartphone, Check, Images } from "lucide-react";

const ICONS = { Sparkles, Utensils, Activity, HeartHandshake, Gift, Smartphone };

export const HotelsPricing = () => {
  const [galleryIndex, setGalleryIndex] = useState(null);
  return (
    <section id="hotels-tarifs" className="relative bg-ocean-dark py-24 sm:py-32 overflow-hidden" data-testid="hotels-pricing-section">
      <div className="absolute top-0 right-0 w-[36rem] h-[36rem] rounded-full bg-ocean-lagoon/20 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[30rem] h-[30rem] rounded-full bg-gold/10 blur-[120px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><Eyebrow>Destinations & formule</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-serif font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
              Nos 4 hôtels partenaires 4★<br className="hidden sm:block" /> & une formule clé en main
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-base sm:text-lg text-white/70">
              Des établissements d'exception sélectionnés pour leur charme, leur service irréprochable
              et leur ancrage naturel en Guadeloupe & Saint-Martin.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {HOTELS.map((h, i) => (
            <Reveal key={h.name} delay={i * 0.08}>
              <button
                type="button"
                onClick={() => setGalleryIndex(i)}
                className="group h-full w-full flex flex-col text-left rounded-3xl overflow-hidden bg-white/[0.04] border border-white/10 transition-all duration-500 hover:border-gold/40 hover:-translate-y-1.5 cursor-pointer"
                data-testid={`card-hotel-${i}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={h.image} alt={h.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ocean-dark to-transparent opacity-60" />
                  <span className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-semibold text-ocean-dark bg-gold rounded-full px-2.5 py-1">
                    <Star className="w-3 h-3 fill-ocean-dark" /> {h.stars}
                  </span>
                  <span className="absolute bottom-3 left-3 flex items-center gap-1.5 text-[11px] font-medium text-white bg-black/35 backdrop-blur-sm rounded-full px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Images className="w-3.5 h-3.5" /> Voir la galerie
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-1.5 text-gold text-xs font-medium">
                    <MapPin className="w-3.5 h-3.5" /> {h.location}
                  </div>
                  <h3 className="mt-2 font-serif text-xl font-semibold text-white leading-tight">{h.name}</h3>
                  <ul className="mt-4 space-y-2">
                    {h.features.map((f, j) => (
                      <li key={j} className="flex gap-2 text-[13px] text-white/65 leading-snug">
                        <Check className="w-3.5 h-3.5 mt-0.5 text-ocean-lagoon flex-shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Pricing box */}
        <Reveal delay={0.1}>
          <div className="mt-16 rounded-[2rem] bg-gradient-to-br from-sand-light to-sand-warm border border-gold/30 p-6 sm:p-10">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <Eyebrow>Formule tout inclus</Eyebrow>
                <div className="mt-4 flex items-end gap-2">
                  <span className="font-serif text-6xl sm:text-7xl font-bold text-ocean">260€</span>
                  <span className="text-muted2 font-medium mb-2">TTC / pers.</span>
                </div>
                <p className="mt-4 text-sm text-muted2 leading-relaxed max-w-xs">
                  Tout est compris. Aucun frais caché. Formule optimisée pour la prise en charge CSE / CE.
                </p>
                <button
                  onClick={() => document.querySelector("#formulaire-devis")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-6 inline-flex rounded-full bg-coral hover:bg-coral-hover text-white font-semibold px-6 py-3.5 text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(230,92,0,0.35)]"
                  data-testid="pricing-cta-button"
                >
                  Obtenir mon devis 260€/pers.
                </button>
              </div>

              <BudgetCalculator />
            </div>

            <div className="mt-8 pt-8 border-t border-ocean/10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {PICTOGRAMS.map((p, i) => {
                const Icon = ICONS[p.icon];
                return (
                  <div key={i} className="flex gap-3 items-start" data-testid={`pictogram-${i}`}>
                    <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-palm-light flex items-center justify-center">
                      <Icon className="w-5 h-5 text-palm" strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className="font-semibold text-ocean text-sm">{p.title}</div>
                      <div className="text-xs text-muted2 mt-0.5 leading-snug">{p.subtitle}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>

      <HotelGallery
        open={galleryIndex !== null}
        onOpenChange={(o) => !o && setGalleryIndex(null)}
        hotel={galleryIndex !== null ? HOTELS[galleryIndex] : null}
        images={galleryIndex !== null ? HOTEL_GALLERIES[galleryIndex] : []}
      />
    </section>
  );
};
